/**
 * Cotação de frete local (tele-entrega por CEP) + endereço (ViaCEP).
 * Usado pela pré-venda (ERP) e helpers de endereço.
 */

import { shippingService } from '../services/api';

/** Cotação online ativa (motor local / shipping_zones). */
export const SHIPPING_CALCULATION_ENABLED = true;

export const SHIPPING_CALC_DISABLED_MESSAGE =
  'Cálculo de frete temporariamente indisponível. Você pode retirar na loja.';

export const SHIPPING_OUT_OF_AREA_MESSAGE =
  'Ainda não entregamos nesse local. Você pode retirar na loja ou falar conosco.';

/** CEP de origem da loja (Pelotas). Override: REACT_APP_SHIPPING_ORIGIN_CEP */
export const SHIPPING_ORIGIN_CEP =
  (process.env.REACT_APP_SHIPPING_ORIGIN_CEP || '96010000').replace(/\D/g, '');

/**
 * @param {string | null | undefined} value
 * @returns {string} apenas dígitos
 */
export function digitsOnlyCep(value) {
  return String(value || '').replace(/\D/g, '').slice(0, 8);
}

/**
 * @param {string | null | undefined} value
 * @returns {string} 00000-000
 */
export function formatCepMask(value) {
  const d = digitsOnlyCep(value);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

/**
 * @param {unknown} raw
 * @returns {number | null}
 */
function deliveryDays(raw) {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Escolhe Standard (mais barato) e Express (mais rápido).
 * Com uma única opção local (tele-entrega), preenche as duas faixas.
 * @param {Array<{ id?: number|string|null, name?: string, company?: string, price?: number, delivery_time?: number|null }>} options
 */
export function pickExpressAndStandard(options) {
  const valid = (Array.isArray(options) ? options : []).filter(
    (o) => o && typeof o.price === 'number' && !Number.isNaN(o.price) && o.price > 0
  );

  if (valid.length === 0) {
    return { express: null, standard: null };
  }

  if (valid.length === 1) {
    return { express: valid[0], standard: valid[0] };
  }

  const byPrice = [...valid].sort((a, b) => a.price - b.price);
  const standard = byPrice[0];

  const byTime = [...valid].sort((a, b) => {
    const ta = deliveryDays(a.delivery_time) ?? 999;
    const tb = deliveryDays(b.delivery_time) ?? 999;
    if (ta !== tb) return ta - tb;
    return b.price - a.price;
  });

  let express = byTime[0];
  if (
    express &&
    standard &&
    String(express.id) === String(standard.id) &&
    byTime.length > 1
  ) {
    express = byTime[1];
  }

  return { express, standard };
}

/**
 * Cotação por CEP (produtos opcionais — o backend usa só a faixa de CEP).
 * @param {string} toCepDigits
 * @param {{ product_id: number, quantity: number }[]} [products]
 */
export async function quoteFreightByCep(toCepDigits, products = []) {
  if (!SHIPPING_CALCULATION_ENABLED) {
    throw new Error(SHIPPING_CALC_DISABLED_MESSAGE);
  }

  const to = digitsOnlyCep(toCepDigits);
  if (to.length !== 8) {
    throw new Error('CEP inválido. Digite 8 dígitos.');
  }

  const response = await shippingService.calculate(
    SHIPPING_ORIGIN_CEP,
    to,
    Array.isArray(products) ? products : []
  );

  if (!response?.success) {
    throw new Error(response?.message || 'Não foi possível calcular o frete.');
  }

  if (response.data?.disabled) {
    throw new Error(response.data.message || SHIPPING_CALC_DISABLED_MESSAGE);
  }

  const options = response.data?.options || [];
  const message =
    response.data?.message ||
    (options.length === 0 ? SHIPPING_OUT_OF_AREA_MESSAGE : null);

  return {
    options,
    mapped: pickExpressAndStandard(options),
    message,
    from: response.data?.from || SHIPPING_ORIGIN_CEP,
    to,
    zone: response.data?.zone || null,
  };
}

/**
 * ViaCEP via backend.
 * @param {string} cep
 * @returns {Promise<{ street: string, neighborhood: string, city: string, regionCode: string, postalCode: string }>}
 */
export async function fetchAddressByCep(cep) {
  const digits = digitsOnlyCep(cep);
  if (digits.length !== 8) {
    throw new Error('CEP inválido. Informe 8 dígitos.');
  }

  const response = await shippingService.getCepInfo(digits);

  if (response?.code === 'invalid') {
    throw new Error('CEP inválido. Informe 8 dígitos.');
  }

  if (response?.success === false) {
    throw new Error(response?.message || 'Não foi possível consultar o CEP.');
  }

  // Sem logradouro na base — não é erro; a cotação por faixa decide a entrega.
  if (response?.found === false || !response?.data) {
    return null;
  }

  const d = response.data;
  return {
    street: d.address || '',
    neighborhood: d.neighborhood || '',
    city: d.city || '',
    regionCode: d.state || '',
    postalCode: digits,
  };
}

/**
 * Label amigável da opção cotada (apenas prazo).
 * @param {{ name?: string, company?: string, delivery_time?: number|null, neighborhood?: string } | null} option
 * @param {'express' | 'standard'} fallback
 */
export function shippingOptionLabel(option, fallback) {
  if (!option) {
    return fallback === 'express' || fallback === 'standard'
      ? 'Informe o CEP'
      : 'Entrega';
  }
  const days = deliveryDays(option.delivery_time);
  if (days != null) {
    return `${days} dia${days === 1 ? '' : 's'} útil${days === 1 ? '' : 'eis'}`;
  }
  return 'Tele-entrega';
}
